import { baseApi } from "./baseApi";

export interface ListingFiltersResponse {
  utm_sources: string[];
  utm_mediums: string[];
  room_types: string[];
}

export interface VerifyLeadExistsResponse {
  lead_id?: string;
  name?: string;
  current_stage?: string;
  created_at?: string;
  email?: string;
  phone?: string;
  whatsapp_phone?: string;
}

export interface TenantTypeOption {
  code: string;
  label: string;
}

export interface TenantTypesResponse {
  industries: string[];
  options: TenantTypeOption[];
}

export const communicationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getChatbotInboxSummary: builder.query({
      query: (payload: {
        startDate: string;
        endDate: string;
        channel?: string;
        campaignId?: string;
        destination_country?: string[];
        source_country?: string[];
        cities?: string[];
        university?: string;
        tags?: string[];
        room_types?: string[];
        property?: string[];
        has_no_property?: boolean;
        utm_sources?: string[];
        utm_medium?: string[];
        attribution_channel?: string[];
        standard_medium?: string[];
        standard_source?: string[];
        device_type?: string[];
        budget?: {
          min_budget?: number;
          max_budget?: number;
          currency?: string;
          duration?: string;
        }[];
        lease?: {
          min_lease?: number;
          max_lease?: number;
          unit?: string;
        }[];
        assigned_agents?: string[];
        lead_type?: string;
        viewing?: boolean;
        repeat_lead?: boolean;
        bot_version?: string[];
        is_online?: boolean;
      }) => {
        // Build query string with repeated params for arrays
        const searchParams = new URLSearchParams();

        searchParams.append("start_date", payload.startDate);
        searchParams.append("end_date", payload.endDate);

        if (payload.channel && payload.channel !== "all")
          searchParams.append("channel", payload.channel);
        if (payload.campaignId)
          searchParams.append("campaign_id", payload.campaignId);

        if (payload.university)
          searchParams.append("university", payload.university);

        // Arrays as repeated query params
        payload.source_country?.forEach((country) =>
          searchParams.append("source_country", country)
        );

        payload.cities?.forEach((city) =>
          searchParams.append("destination_city", city)
        );

        payload.destination_country?.forEach((country) =>
          searchParams.append("destination_country", country)
        );
        payload.tags?.forEach((tag) => searchParams.append("tags", tag));
        payload.room_types?.forEach((rt) =>
          searchParams.append("room_types", rt)
        );
        if (payload.has_no_property) {
          searchParams.append("has_no_property", "true");
        } else {
          payload.property?.forEach((p) => searchParams.append("properties", p));
          if (payload.has_no_property === false) {
            searchParams.append("has_no_property", "false");
          }
        }
        payload.utm_sources?.forEach((src) =>
          searchParams.append("utm_source", src)
        );
        payload.utm_medium?.forEach((med) =>
          searchParams.append("utm_medium", med)
        );
        payload.attribution_channel?.forEach((channel) =>
          searchParams.append("attribution_channel", channel)
        );
        payload.standard_medium?.forEach((medium) =>
          searchParams.append("standard_medium", medium)
        );
        payload.standard_source?.forEach((source) =>
          searchParams.append("standard_source", source)
        );
        payload.device_type?.forEach((device) =>
          searchParams.append("device_type", device)
        );

        // Budget and lease as JSON strings
        if (payload.budget && payload.budget.length > 0) {
          searchParams.append("budget", JSON.stringify(payload.budget));
        }
        if (payload.lease && payload.lease.length > 0) {
          searchParams.append("lease", JSON.stringify(payload.lease));
        }

        // New filters: assigned agents, lead type, viewing, repeat lead
        if (payload.assigned_agents && payload.assigned_agents.length > 0) {
          searchParams.append(
            "assigned_agents",
            JSON.stringify(payload.assigned_agents)
          );
        }
        if (payload.viewing !== undefined && payload.viewing) {
          searchParams.append("has_viewing", "true");
        }
        if (payload.repeat_lead !== undefined && payload.repeat_lead) {
          searchParams.append("exclude_repeated_leads", "true");
        }

        if (payload.lead_type) {
          searchParams.append("tenant_type", payload.lead_type);
        }

        if (payload.bot_version && payload.bot_version.length > 0) {
          searchParams.append("bot_version", JSON.stringify(payload.bot_version));
        }
        if (payload.is_online !== undefined) {
          searchParams.append("is_online", String(payload.is_online));
        }

        return {
          url: `/dashboard/communication/chatbot-inbox-summary?${searchParams.toString()}`,
          method: "GET",
        };
      },
    }),

    getLeadList: builder.query({
      query: (payload: {
        channel?: string;
        chatType: string;
        startDate?: string;
        endDate?: string;
        cursor?: string;
        campaign_id?: string;
        category_dropdown?: string;
        outcome_dropdown?: string;
        destination_country?: string[];
        source_country?: string[];
        cities?: string[];
        university?: string;
        tags?: string[];
        room_types?: string[];
        property?: string[];
        has_no_property?: boolean;
        utm_sources?: string[];
        utm_medium?: string[];
        attribution_channel?: string[];
        standard_medium?: string[];
        standard_source?: string[];
        device_type?: string[];
        budget?: {
          min_budget?: number;
          max_budget?: number;
          currency?: string;
          duration?: string;
        }[];
        lease?: {
          min_lease?: number;
          max_lease?: number;
          unit?: string;
        }[];
        search_query?: string;
        limit?: number;
        assigned_agents?: string[];
        lead_type?: string;
        viewing?: boolean;
        repeat_lead?: boolean;
        is_crm?: boolean;
        exclude_repeated_leads?: boolean;
        exclude_lead_id?: string;
        bot_version?: string[];
        is_online?: boolean;
      }) => {
        // Build query string with repeated params for arrays
        const searchParams = new URLSearchParams();

        if (payload.startDate)
          searchParams.append("start_date", payload.startDate);
        if (payload.endDate)
          searchParams.append("end_date", payload.endDate);
        searchParams.append("chat_type", payload.chatType);

        if (payload.limit && payload.limit > 0)
          searchParams.append("limit", payload.limit.toString());
        if (payload.cursor) searchParams.append("cursor", payload.cursor);
        if (payload.channel) searchParams.append("channel", payload.channel);
        if (payload.campaign_id)
          searchParams.append("campaign_id", payload.campaign_id);

        if (payload.university)
          searchParams.append("university", payload.university);

        if (payload.is_crm) {
          searchParams.append("is_crm", "true");
        }

        // Arrays as repeated query params

        payload.source_country?.forEach((country) =>
          searchParams.append("source_country", country)
        );
        payload.destination_country?.forEach((country) =>
          searchParams.append("destination_country", country)
        );
        payload.cities?.forEach((city) =>
          searchParams.append("destination_city", city)
        );
        payload.tags?.forEach((tag) => searchParams.append("tags", tag));
        payload.room_types?.forEach((rt) =>
          searchParams.append("room_types", rt)
        );
        if (payload.has_no_property) {
          searchParams.append("has_no_property", "true");
        } else {
          payload.property?.forEach((p) => searchParams.append("properties", p));
          if (payload.has_no_property === false) {
            searchParams.append("has_no_property", "false");
          }
        }
        payload.utm_sources?.forEach((src) =>
          searchParams.append("utm_source", src)
        );
        payload.utm_medium?.forEach((med) =>
          searchParams.append("utm_medium", med)
        );
        payload.attribution_channel?.forEach((channel) =>
          searchParams.append("attribution_channel", channel)
        );
        payload.standard_medium?.forEach((medium) =>
          searchParams.append("standard_medium", medium)
        );
        payload.standard_source?.forEach((source) =>
          searchParams.append("standard_source", source)
        );
        payload.device_type?.forEach((device) =>
          searchParams.append("device_type", device)
        );

        // Budget and lease as JSON strings
        if (payload.budget && payload.budget.length > 0) {
          searchParams.append("budget", JSON.stringify(payload.budget));
        }
        if (payload.lease && payload.lease.length > 0) {
          searchParams.append("lease", JSON.stringify(payload.lease));
        }

        if (payload.search_query)
          searchParams.append("search_query", payload.search_query);

        // New filters: assigned agents, lead type, viewing, repeat lead
        if (payload.assigned_agents && payload.assigned_agents.length > 0) {
          searchParams.append(
            "assigned_agents",
            JSON.stringify(payload.assigned_agents)
          );
        }
        if (payload.viewing !== undefined && payload.viewing) {
          searchParams.append("has_viewing", "true");
        }
        if (payload.repeat_lead !== undefined && payload.repeat_lead) {
          searchParams.append("exclude_repeated_leads", "true");
        }
        if (payload.exclude_repeated_leads) {
          searchParams.append("exclude_repeated_leads", "true");
        }
        if (payload.exclude_lead_id) {
          searchParams.append("exclude_lead_id", payload.exclude_lead_id);
        }
        if (payload.lead_type) {
          searchParams.append("tenant_type", payload.lead_type);
        }

        if (payload.bot_version && payload.bot_version.length > 0) {
          searchParams.append("bot_version", JSON.stringify(payload.bot_version));
        }
        if (payload.is_online !== undefined) {
          searchParams.append("is_online", String(payload.is_online));
        }

        return {
          url: `/dashboard/communication/chatbot-inbox-listing?${searchParams.toString()}`,
          method: "GET",
        };
      },
    }),

    getLeadInboxDetails: builder.query({
      query: (payload: {
        id: string;
        lead_type: string;
        channel?: string;
      }) => ({
        url: `/dashboard/communication/chatbot-inbox/${payload.id}?${
          payload.channel && payload.channel !== "all" ? `channel=${payload.channel}&` : ""
        }lead_type=${payload.lead_type}`,
        method: "GET",
      }),
    }),

    searchLead: builder.mutation({
      query: (params: { query: string; channel: string }) => ({
        url: `/dashboard/communication/search`,
        method: "GET",
        params: {
          query: params.query,
          ...(params.channel !== "all" && { channel: params.channel }),
        },
      }),
    }),

    searchCampaignLeads: builder.mutation({
      query: (params: { query: string; campaign_id: string }) => ({
        url: `/dashboard/campaigns/communication/${params.campaign_id}/search?query=${params.query}`,
        method: "GET",
      }),
    }),

    sendChatbotMessage: builder.mutation({
      query: (params: {
        id: string;
        chatMessage: {
          content: string;
          lead_type: string;
          subject?: string;
          cc?: string[];
          bcc?: string[];
          attachments?: {
            s3_key: string;
            file_name: string;
            content_type: string;
            size_in_bytes: number;
          }[];
        };
        channel: string;
      }) => ({
        url: `/dashboard/leads/${params.id}/send-message/${params.channel}`,
        method: "POST",
        body: {
          content: params.chatMessage.content,
          lead_type: params.chatMessage.lead_type,
          subject: params.chatMessage.subject,
          cc: params.chatMessage.cc,
          bcc: params.chatMessage.bcc,
          attachments: params.chatMessage.attachments,
        },
      }),
      invalidatesTags: ["SendEmail", "EngagementSummaryCount"],
    }),

    getCampaignLeadDetails: builder.query({
      query: (leadId: string) => ({
        url: `/dashboard/communication/chatbot-inbox/${leadId}/campaign-lead-details`,
        method: "GET",
      }),
    }),

    getActivityLogs: builder.query({
      query: ({
        id,
        lead_type = "lead",
      }: {
        id: string;
        lead_type: string;
      }) => ({
        url: `dashboard/leads/${id}/logs?lead_type=${lead_type}`,
        method: "GET",
      }),
      providesTags: [{ type: "CommunicationLogs", id: "ActivityLogs" }],
    }),

    updateLeadDetails: builder.mutation({
      query: (params: {
        lead_id: string;
        lead_type?: string;
        lead_data: object;
      }) => ({
        url: `/dashboard/lead/${params.lead_id}?type=${params.lead_type}`,
        method: "PATCH",
        body: params.lead_data,
      }),
      invalidatesTags: [{ type: "CommunicationLogs", id: "ActivityLogs" }],
    }),

    getLeadCountries: builder.query({
      query: () => ({
        url: `/dashboard/pms/countries`,
        method: "GET",
      }),
    }),

    getLeadCities: builder.query({
      query: (countryIds: string | number) => ({
        url: `/dashboard/pms/cities?country_id=${countryIds}`,
        method: "GET",
      }),
    }),

    getLeadUniversities: builder.query({
      query: (id: string | number) => ({
        url: `/dashboard/pms/universities?city_id=${id}`,
        method: "GET",
      }),
    }),

    getUtmSources: builder.query({
      query: () => ({
        url: "/users/utm-source",
        method: "GET",
      }),
    }),

    getUtmMedium: builder.query({
      query: () => ({
        url: "/users/utm-medium",
        method: "GET",
      }),
    }),

    getChoosenPropsOptions: builder.query({
      query: () => ({
        url: "/settings/lead-form/field-options/choosen_props",
        method: "GET",
      }),
    }),

    getLeadDetailTags: builder.query({
      query: () => ({
        url: `/lms/tags/`,
        method: "GET",
      }),
    }),

    toggleCommunicationChannel: builder.mutation({
      query: (params: {
        lead_id: string;
        active: boolean;
        lead_type: string;
        channel: string;
      }) => ({
        url: `/dashboard/leads/${params.lead_id}/update-communication-channel-status/${params.channel}`,
        method: "POST",
        body: {
          active: params.active,
          lead_type: params.lead_type,
        },
      }),
      invalidatesTags: [{ type: "LeadDetails", id: "LeadLogs" }],
    }),

    deleteLead: builder.mutation({
      query: (params: { leadId: string }) => ({
        url: `/lms/leads/client/lead/${params?.leadId}`,
        method: "DELETE",
      }),
    }),
    getAttachment: builder.mutation<string, { s3_key: string }>({
      query: ({ s3_key }) => ({
        url: `/attachments/?s3_key=${s3_key}`,
        method: "GET",
      }),
    }),

    getChatbotOutboxDetails: builder.query({
      query: (payload: { campaignId: string; leadId: string }) => ({
        url: `/dashboard/communication/chatbot-outbox/${payload.campaignId}/${payload.leadId}`,
        method: "GET",
      }),
    }),

    getCampaignInboxSummary: builder.query({
      query: ({
        channel,
        campaignId,
        category_dropdown,
        outcome_dropdown,
      }) => ({
        url: `/dashboard/campaigns/communication/lead-listing-summary`,
        method: "GET",
        params: {
          ...(channel !== "all" && { channel: channel }),
          campaign_id: campaignId,
          ...(category_dropdown?.length > 0 && {
            category_dropdown: category_dropdown,
          }),
          ...(outcome_dropdown?.length > 0 && {
            outcome_dropdown: outcome_dropdown,
          }),
        },
      }),
    }),

    getCampaignLeadListing: builder.mutation({
      query: (body: {
        campaign_id: string;
        channel: string;
        category_dropdown: string[];
        outcome_dropdown: string[];
        cursor?: string;
      }) => ({
        url: "/dashboard/campaigns/communication/lead-listing",
        method: "POST",
        body: {
          campaign_id: body.campaign_id,
          ...(body.channel &&
            body.channel !== "all" && { channel: body.channel }),
          ...(body.category_dropdown &&
            body.category_dropdown.length > 0 && {
              category_dropdown: body.category_dropdown,
            }),
          ...(body.outcome_dropdown &&
            body.outcome_dropdown.length > 0 && {
              outcome_dropdown: body.outcome_dropdown,
            }),
          ...(body.cursor && { cursor: body.cursor }),
        },
      }),
    }),
    getSources: builder.mutation({
      query: (body: { chunk_db_ids: string[] }) => ({
        url: "/playground/get-chunks",
        method: "POST",
        body,
      }),
    }),
    addCustomQA: builder.mutation({
      query: (body: {
        chunks: Array<{
          question: string;
          answer: string;
          namespace: {
            name: string;
            id: number | string;
          };
        }>;
      }) => ({
        url: "/playground/create-chunk",
        method: "POST",
        body,
      }),
    }),
    updateSource: builder.mutation({
      query: (body: {
        chunk_db_id: string;
        question: string;
        answer: string;
      }) => ({
        url: "/playground/update-chunk",
        method: "POST",
        body,
      }),
    }),
    downloadDocument: builder.mutation<
      { url: string },
      { docId: string; documentType: string }
    >({
      query: ({ docId, documentType }) => ({
        url: `/playground/download-document?document_id=${docId}&document_type=${documentType}`,
        method: "GET",
      }),
    }),
    reingestDocument: builder.mutation({
      query: (body: { doc_id: string; s3_uri: string; file_name: string }) => ({
        url: "/playground/reingest-document",
        method: "POST",
        body,
      }),
    }),
    // Bulk upload endpoint
    bulkUploadDocuments: builder.mutation({
      query: ({
        files,
        documentInfo,
      }: {
        files: File[];
        documentInfo?: {
          description: string;
          path: string;
          namespace: {
            name: string;
            id: string;
          };
        };
      }) => {
        const formData = new FormData();

        // Determine which type of request this is
        if (files.length > 0) {
          // First phase: uploading files - append all files with the same field name
          files.forEach((file) => {
            formData.append("files", file);
          });
        } else if (documentInfo) {
          // Second phase: processing with document info
          formData.append("document_info", JSON.stringify(documentInfo));
        }

        return {
          url: "/playground/upload-bulk-documents",
          method: "POST",
          body: formData,
          formData: true,
        };
      },
    }),

    // Create Ticket endpoint
    createTicket: builder.mutation({
      query: (payload: {
        leadId: string;
        modality: string;
        data: {
          student_name: string;
          student_email: string | null;
          student_phone: string | null;
          category: string;
          description: string;
          subject: string;
        };
      }) => ({
        url: `/dashboard/leads/${payload.leadId}/create-ticket/${payload.modality}`,
        method: "POST",
        body: payload.data,
      }),
    }),

    // Get Ticket Category Options endpoint
    getTicketCategoryOptions: builder.query({
      query: () => ({
        url: `/dashboard/customers/get-categories`,
        method: "GET",
      }),
    }),

    getListingFilters: builder.query<
      ListingFiltersResponse,
      { start_date?: string; end_date?: string; chat_type?: string }
    >({
      query: ({ start_date, end_date, chat_type }) => ({
        url: `/dashboard/listing-filters`,
        method: "GET",
        params: {
          ...(start_date && { start_date }),
          ...(end_date && { end_date }),
          ...(chat_type && { chat_type }),
        },
      }),
    }),

    sendMentionMessage: builder.mutation({
      query: (params: {
        lead_id: string;
        content: string;
        mention_user_ids: string[];
      }) => ({
        url: `/dashboard/leads/${params.lead_id}/mention-users`,
        method: "POST",
        body: {
          content: params.content,
          mention_user_ids: params.mention_user_ids,
        },
      }),
    }),

    getCommunicationSources: builder.mutation({
      query: (body: {
        lead_id: string;
        lead_type: string;
        message_id: string;
        is_playground?: boolean;
      }) => ({
        url: "/dashboard/communication/sources",
        method: "POST",
        body,
      }),
    }),
    getCommunicationAnalytics: builder.query({
      query: (filters: {
        date_start: string;
        date_end: string;
        channel: string;
      }) => ({
        url: `/dashboard/chatbot-analytics/${filters.channel}${
          filters.date_start
            ? `?start_date=${filters.date_start}&end_date=${filters.date_end}`
            : ""
        }`,
        method: "GET",
      }),
    }),
    getCommunicationAnalyticsWebsiteVisitors: builder.query({
      query: (filters: {
        date_start: string;
        date_end: string;
        channel: string;
      }) => ({
        url: `/dashboard/visitors-trend/${filters.channel}${
          filters.date_start
            ? `?start_date=${filters.date_start}&end_date=${filters.date_end}`
            : ""
        }`,
        method: "GET",
      }),
    }),
    sendEmailMessage: builder.mutation({
      query: (params: {
        id: string;
        primary_id: string;
        chatMessage: {
          content: string;
          lead_type: string;
          subject?: string;
          cc?: string[];
          bcc?: string[];
          attachments?: {
            s3_key: string;
            file_name: string;
            content_type: string;
            size_in_bytes: number;
          }[];
        };
        channel: string;
        nylas_thread_id?: string;
        from?: string;
        unsubscribe_enabled?: boolean;
      }) => ({
        url: `/dashboard/leads/send-message`,
        method: "POST",
        body: {
          lead_id: params.id,
          primary_id: params.primary_id,
          content: params.chatMessage.content,
          subject: params.chatMessage.subject,
          cc: params.chatMessage.cc,
          bcc: params.chatMessage.bcc,
          attachments: params.chatMessage.attachments,
          lead_listing: false,
          channel: params.channel,
          ...(params.nylas_thread_id && {
            nylas_thread_id: params.nylas_thread_id,
          }),
          ...(params.from && { from_email: params.from }),
          ...(params.unsubscribe_enabled && {
            unsubscribe_enabled: params.unsubscribe_enabled,
          }),
        },
      }),
      invalidatesTags: ["SendEmail", "EngagementSummaryCount"],
    }),
    getVerbaCallToken: builder.mutation({
      query: (params: {
        leadId: string;
        leadType: string;
        leadListing?: boolean;
      }) => ({
        url: params.leadId
          ? `/dashboard/leads/${params.leadId}/send-message/call`
          : `/dashboard/leads/generate-call-token`,
        method: "POST",
        body: {
          lead_type: params?.leadType,
          content: "play_call",
          lead_listing: params?.leadListing ?? false,
        },
      }),
    }),
    getIntegrationsPresent: builder.query({
      query: () => ({
        url: `/dashboard/get-integrations-present`,
        method: "GET",
      }),
    }),
    getLeadFormFields: builder.query({
      query: () => ({
        url: `/settings/lead-form/form`,
        method: "GET",
      }),
      providesTags: [{ type: "Settings", id: "GetLeadFormSettings" }],
    }),
    getIntegrationEmails: builder.query<
      { email: string; is_default: boolean }[],
      { user_email?: string; source?: string }
    >({
      query: ({ user_email, source }) => ({
        url: `/integration/emails`,
        method: "GET",
        params: source ? { source } : { user_email },
      }),
      transformResponse: (response: {
        emails: { email: string; is_default: boolean }[];
      }) => response.emails,
    }),
    verifyLeadExists: builder.mutation<
      VerifyLeadExistsResponse,
      { email?: string; phone?: string; phone_country_code?: string }
    >({
      query: (params) => ({
        url: `/lms/leads/lookup`,
        method: "GET",
        params,
      }),
    }),
    getTenantTypes: builder.query<TenantTypesResponse, void>({
      query: () => "/lms/tenant-types",
    }),
    bulkArchiveChats: builder.mutation<
      void,
      { scope: "user" | "client"; chat_entity_ids: string[] }
    >({
      query: (body) => ({
        url: "/dashboard/communication/chats/bulk-archive",
        method: "POST",
        body,
      }),
    }),
    bulkUnarchiveChats: builder.mutation<
      void,
      { scope: "user" | "client"; chat_entity_ids: string[] }
    >({
      query: (body) => ({
        url: "/dashboard/communication/chats/bulk-unarchive",
        method: "POST",
        body,
      }),
    }),
    archiveChat: builder.mutation<
      void,
      { chatEntityId: string; scope: "user" | "client" }
    >({
      query: ({ chatEntityId, scope }) => ({
        url: `/dashboard/communication/chats/${chatEntityId}/archive`,
        method: "POST",
        body: { scope },
      }),
    }),
    unarchiveChat: builder.mutation<
      void,
      { chatEntityId: string; scope: "user" | "client" }
    >({
      query: ({ chatEntityId, scope }) => ({
        url: `/dashboard/communication/chats/${chatEntityId}/unarchive`,
        method: "POST",
        body: { scope },
      }),
    }),
    getBotVersions: builder.query<
      { version: string; name: string }[],
      void
    >({
      query: () => ({
        url: "/client_config/bot_versions",
        method: "GET",
      }),
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetChatbotInboxSummaryQuery,
  useGetLeadListQuery,
  useLazyGetLeadListQuery,
  useLazyGetLeadInboxDetailsQuery,
  useSearchLeadMutation,
  useSendChatbotMessageMutation,
  useGetCampaignLeadDetailsQuery,
  useLazyGetActivityLogsQuery,
  useLazyGetCampaignLeadDetailsQuery,
  useUpdateLeadDetailsMutation,
  useGetLeadCountriesQuery,
  useGetLeadCitiesQuery,
  useGetLeadUniversitiesQuery,
  useGetUtmSourcesQuery,
  useGetUtmMediumQuery,
  useGetChoosenPropsOptionsQuery,
  useGetLeadDetailTagsQuery,
  useToggleCommunicationChannelMutation,
  useDeleteLeadMutation,
  useGetAttachmentMutation,
  useGetChatbotOutboxDetailsQuery,
  useLazyGetChatbotOutboxDetailsQuery,
  useGetCampaignInboxSummaryQuery,
  useGetCampaignLeadListingMutation,
  useGetSourcesMutation,
  useAddCustomQAMutation,
  useUpdateSourceMutation,
  useDownloadDocumentMutation,
  useReingestDocumentMutation,
  useSearchCampaignLeadsMutation,
  useCreateTicketMutation,
  useGetTicketCategoryOptionsQuery,
  useGetListingFiltersQuery,
  useSendMentionMessageMutation,
  useGetCommunicationSourcesMutation,
  useGetCommunicationAnalyticsQuery,
  useGetCommunicationAnalyticsWebsiteVisitorsQuery,
  useSendEmailMessageMutation,
  useGetVerbaCallTokenMutation,
  useGetIntegrationsPresentQuery,
  useGetLeadFormFieldsQuery,
  useGetIntegrationEmailsQuery,
  useVerifyLeadExistsMutation,
  useGetTenantTypesQuery,
  useBulkArchiveChatsMutation,
  useBulkUnarchiveChatsMutation,
  useArchiveChatMutation,
  useUnarchiveChatMutation,
  useGetBotVersionsQuery,
} = communicationApi;
